import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useMutation from 'hooks/useMutation'

// Mismo tope que api/src/beta/router.js — sin esto el usuario recién se
// entera de que el texto es demasiado largo al mandarlo (400 del backend),
// en vez de no poder tipear de más desde el vamos.
const MAX_FREE_TEXT_LENGTH = 1000

/**
 * Módulo genérico de feedback de features beta (pedido de Nacho, 2026-08-10)
 * — arranca usándose en ARCA, pensado para reusarse con cualquier feature
 * futura sin tocar este componente, solo pasando otro `featureKey` (el slug
 * que ya conoce el backend) + `featureName` (el nombre visible que el
 * usuario reconoce en pantalla, para el título del modal).
 *
 * Dos formas de abrir el mismo form:
 *   1. Automática: el caller sube `autoTriggerSignal` a un valor nuevo justo
 *      cuando ocurre la acción que amerita pedir feedback (ej. factura
 *      emitida). Este componente intenta "reclamar" el turno semanal
 *      (POST /beta/:featureKey/claim-prompt, atómico en el backend) — si
 *      ya se le mostró a este usuario en los últimos 7 días (en CUALQUIER
 *      dispositivo, el cooldown vive en el backend), no pasa nada.
 *   2. Manual: quien tenga la ref puede llamar `openManual()` para abrir el
 *      form sin pasar por el cooldown — el usuario puede opinar cuando
 *      quiera (ver más abajo, "Acceso voluntario").
 *
 * "Ahora no" en el popup automático SÍ manda un registro al backend
 * (dismissed:true) — no reinicia el cooldown por su cuenta: el cooldown ya
 * quedó marcado por claim-prompt en el momento en que se mostró el popup.
 *
 * Acceso voluntario (pedido de Nacho, 2026-08-10, segunda vuelta): sin
 * solapa fija propia — el disparador es el BETA badge de la pantalla
 * (BetaBadge con onClick), en el mismo lugar donde la función ya está
 * visible, siempre disponible mientras el usuario tiene la función
 * delante, sin depender de ganarle la carrera al auto-trigger. openManual
 * se expone vía ref para que el caller pueda conectarlo al onClick del
 * badge sin que este componente tenga que saber nada de dónde vive ese
 * badge en el árbol (title de un modal, header de una página, etc.).
 */
export const BetaFeedback = forwardRef(function BetaFeedback({ featureKey, featureName, autoTriggerSignal = null }, ref) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('manual') // 'auto' | 'manual' — determina si aparece "Ahora no"
  const [done, setDone] = useState(false)
  const [rating, setRating] = useState(null)
  const [nps, setNps] = useState('')
  const [liked, setLiked] = useState('')
  const [missing, setMissing] = useState('')
  const [comment, setComment] = useState('')

  const claimUrl = window._l(`/beta/${featureKey}/claim-prompt`).substr(1)
  const feedbackUrl = window._l(`/beta/${featureKey}/feedback`).substr(1)

  const [mutateClaim] = useMutation(claimUrl, {
    // Bug real evitado a propósito (esta instancia hoy nunca combina solapa
    // manual + auto-trigger a la vez — showTab=false en Facturar.js, sin
    // autoTriggerSignal en Arca.js/ArcaBilling.js — pero el componente es
    // genérico y se va a reusar en otra feature futura que sí podría
    // combinar ambos): si el usuario ya tiene el modal abierto a mano
    // (reason:'manual', puede estar a mitad de escribir algo) y en ESE
    // momento se resuelve un auto-trigger en cola, `!open` evita que
    // openModal('auto') lo pise — resetForm() borraría lo que ya tenía
    // tipeado y le cambiaría el modal debajo de las manos agregándole
    // "Ahora no" de la nada.
    onSuccess: payload => { if (payload?.claimed && !open) openModal('auto') },
    // Silencioso a propósito: si falla el claim (red, backend caído), el
    // popup automático simplemente no aparece esta vez — no hay nada que
    // avisarle al usuario sobre un feedback opcional que ni pidió.
    onError: () => {},
  })

  const [mutateFeedback, feedbackMutation] = useMutation(feedbackUrl, {
    onSuccess: () => setDone(true),
  })

  // Dispara el intento de claim cuando autoTriggerSignal cambia a un valor
  // no-nulo — el array de dependencias de useEffect ya evita relanzar
  // mientras el padre mantenga el mismo valor entre renders (comparación por
  // referencia de React, sin necesidad de un ref propio para eso). El padre
  // solo debe pasar un valor real justo después del evento que amerita pedir
  // feedback (ej. el id de la factura recién emitida), nunca por default.
  useEffect(() => {
    if (autoTriggerSignal == null) return
    mutateClaim({})
  }, [autoTriggerSignal]) // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setRating(null); setNps(''); setLiked(''); setMissing(''); setComment(''); setDone(false)
  }

  const openModal = openReason => { resetForm(); setReason(openReason); setOpen(true) }
  const closeModal = () => setOpen(false)

  // deps: [] a propósito — openModal solo usa setters de useState (siempre
  // estables, sin closures sobre valores que puedan quedar viejos), así que
  // no hace falta recrear el objeto expuesto en cada render.
  useImperativeHandle(ref, () => ({ openManual: () => openModal('manual') }), []) // eslint-disable-line react-hooks/exhaustive-deps

  // Pedido de Juan Cruz (comentario en la card de Trello "Módulo de Feedback
  // BETA reusable", 2026-08-20): rating y NPS — las dos preguntas cerradas —
  // pasan a ser obligatorias, para asegurar una señal cuantitativa mínima en
  // TODO feedback recibido. Los 3 campos de texto libre quedan opcionales a
  // propósito (razón del propio Juan Cruz): exigirlos empuja a completarlos
  // con basura solo para poder enviar, o a abandonar el form directamente.
  // No aplica a "Ahora no" (dismiss), que es justamente la vía para "no
  // tengo nada para decir ahora".
  const hasRequiredFields = rating != null && nps !== ''

  const submit = () => {
    if (!hasRequiredFields) return
    mutateFeedback({
      dismissed: false,
      rating: rating || undefined,
      nps: nps === '' ? undefined : Number(nps),
      liked: liked.trim() || undefined,
      missing: missing.trim() || undefined,
      comment: comment.trim() || undefined,
    })
  }

  // Se guarda igual que un feedback real (dismissed:true) — el cooldown de
  // 7 días ya se marcó al mostrarse el popup (claim-prompt), esto solo deja
  // registro de que el usuario activamente lo postergó.
  const dismiss = () => { mutateFeedback({ dismissed: true }); closeModal() }

  return (
    <>
      {open && (
        <div
          className="modal show d-flex align-items-center justify-content-center"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', minHeight: '100vh', zIndex: 1050 }}
        >
          {/* Fix responsive (2026-08-14): minWidth:380px es más ancho que un
              celular angosto real (360-375px) — el modal se salía de la
              pantalla sin ningún scroll habilitado para alcanzarlo. Modal
              de feedback reusado en toda la app. */}
          <div className="modal-dialog" role="document" style={{ minWidth: 'min(380px, calc(100vw - 32px))', maxWidth: '460px' }}>
            <div className="modal-content">
              <div className="modal-body p-4">
                <div className="d-flex justify-content-between mb-3">
                  {/* Bug real de UX (pedido de Nacho, 2026-08-10, tercera
                      vuelta): el modal nunca nombraba la función de la que
                      hablaba — "¿Qué te pareció esta función?" sin contexto,
                      confuso si el profesional llega a este modal sin
                      recordar que venía de una pantalla marcada BETA.
                      featureName (no featureKey): un slug interno tipo
                      "arca" no es lo que el usuario reconoce en pantalla —
                      el caller pasa el mismo nombre visible que ya usa en su
                      propio título/badge. */}
                  <h5 className="mb-0">{t('beta_feedback_title', '¿Qué te pareció "{{featureName}}"?', { featureName })}</h5>
                  <button type="button" className="btn-close" aria-label={t('close', 'Cerrar')} onClick={closeModal} />
                </div>

                {done ? (
                  <div className="alert alert-success small mb-0">
                    {t('beta_feedback_thanks', '¡Gracias por tu feedback! Nos ayuda un montón mientras seguimos mejorando esta función.')}
                    <div className="mt-2 text-end">
                      <button className="btn btn-sm btn-outline-secondary" onClick={closeModal}>{t('close', 'Cerrar')}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="small text-muted mb-3">
                      {t('beta_feedback_intro', 'Esta función está en beta — todavía la estamos probando con un grupo chico de equipos. La calificación y la recomendación son obligatorias; el resto es opcional.')}
                    </p>

                    <div className="mb-3">
                      <label className="form-label small mb-1">
                        {t('beta_feedback_rating', '¿Qué tan útil te resultó?')} <span className="text-danger" aria-hidden="true">*</span>
                      </label>
                      <div className="d-flex gap-1">
                        {[1, 2, 3, 4, 5].map(value => (
                          <button
                            key={value}
                            type="button"
                            data-testid={`beta-feedback-rating-${value}`}
                            aria-label={t('beta_feedback_rating_value', '{{value}} de 5', { value })}
                            aria-pressed={rating === value}
                            className={`btn btn-sm ${rating === value ? 'btn-warning' : 'btn-outline-secondary'}`}
                            onClick={() => setRating(value)}
                          >
                            {value}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Bug real (auditoría 2026-08-16): ids literales sin scoping —
                        con 2+ instancias montadas a la vez (ej. HCE Summary +
                        Consultations + Odontograma, las tres con
                        BetaFeedback en el mismo árbol de EHR.js) resultaban en
                        ids DOM duplicados, rompiendo la asociación
                        label[htmlFor]: un label de un modal visible podía
                        terminar apuntando al control de un modal oculto.
                        Se escopea cada id/htmlFor con featureKey, que es único
                        por instancia. */}
                    <div className="mb-3">
                      <label htmlFor={`beta-feedback-nps-${featureKey}`} className="form-label small mb-1">
                        {t('beta_feedback_nps', '¿Qué probabilidad hay de que la recomiendes? (0 a 10)')} <span className="text-danger" aria-hidden="true">*</span>
                      </label>
                      <select id={`beta-feedback-nps-${featureKey}`} className="form-select form-select-sm" style={{ maxWidth: '120px' }} value={nps} onChange={event => setNps(event.target.value)}>
                        <option value="">—</option>
                        {Array.from({ length: 11 }, (_, value) => value).map(value => (
                          <option key={value} value={value}>{value}</option>
                        ))}
                      </select>
                    </div>

                    <div className="mb-2">
                      <label htmlFor={`beta-feedback-liked-${featureKey}`} className="form-label small mb-1">{t('beta_feedback_liked', '¿Qué te gustó?')}</label>
                      <textarea id={`beta-feedback-liked-${featureKey}`} className="form-control form-control-sm" rows="2" maxLength={MAX_FREE_TEXT_LENGTH} value={liked} onChange={event => setLiked(event.target.value)} />
                    </div>

                    <div className="mb-2">
                      <label htmlFor={`beta-feedback-missing-${featureKey}`} className="form-label small mb-1">{t('beta_feedback_missing', '¿Qué te faltó o te trabó?')}</label>
                      <textarea id={`beta-feedback-missing-${featureKey}`} className="form-control form-control-sm" rows="2" maxLength={MAX_FREE_TEXT_LENGTH} value={missing} onChange={event => setMissing(event.target.value)} />
                    </div>

                    <div className="mb-3">
                      <label htmlFor={`beta-feedback-comment-${featureKey}`} className="form-label small mb-1">{t('beta_feedback_comment', 'Algo más que quieras contarnos')}</label>
                      <textarea id={`beta-feedback-comment-${featureKey}`} className="form-control form-control-sm" rows="2" maxLength={MAX_FREE_TEXT_LENGTH} value={comment} onChange={event => setComment(event.target.value)} />
                    </div>

                    {feedbackMutation.isError && (
                      <div className="alert alert-danger py-2 small">{t('beta_feedback_error', 'No pudimos enviar tu feedback — intentá de nuevo en un rato.')}</div>
                    )}

                    <div className="d-flex justify-content-end gap-2">
                      {reason === 'auto' && (
                        <button className="btn btn-sm btn-link text-muted" onClick={dismiss}>
                          {t('beta_feedback_dismiss', 'Ahora no')}
                        </button>
                      )}
                      <button className="btn btn-sm btn-primary" onClick={submit} disabled={feedbackMutation.isLoading || !hasRequiredFields}>
                        {feedbackMutation.isLoading ? t('beta_feedback_sending', 'Enviando...') : t('beta_feedback_submit', 'Enviar feedback')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
})

export default BetaFeedback
